import type { HeatmapMetricId } from '@/utils/heatmapColors';

export const WEEKDAY_ORDER = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
] as const;

export const HEATMAP_WEEK_DAYS = WEEKDAY_ORDER.length;

export type HeatmapMetrics = Record<HeatmapMetricId, number | null>;

export type HeatmapDay = {
  day: string;
  date: string;
};

export type HeatmapCell = {
  day: string;
  date: string;
  rowCount: number;
  aggregated: HeatmapMetrics;
};

export type HeatmapHour = {
  hour: number;
  cells: HeatmapCell[];
};

export type ParsedHeatmap = {
  days: HeatmapDay[];
  hours: HeatmapHour[];
  activeBuckets: number;
  rowCount: number;
  timezone: string;
  dayCount: number;
};

export type HeatmapWeekRange = {
  startIso: string;
  endIso: string;
  label: string;
  dayCount: number;
  isValid: boolean;
};

const EMPTY_METRICS: HeatmapMetrics = {
  roas: null,
  acos: null,
  cpc: null,
  spend: null,
  sales: null,
  units: null,
  clicks: null,
  impressions: null,
  ctr: null,
  conversion: null,
  asp: null,
};

function numberValue(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return 0;
}

function localDateKey(value: Date): string {
  const year = value.getFullYear();
  const month = `${value.getMonth() + 1}`.padStart(2, '0');
  const day = `${value.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** Local Monday 00:00 for the week containing `value`. */
export function startOfWeekMonday(value: Date): Date {
  const result = new Date(value);
  result.setHours(0, 0, 0, 0);
  const day = result.getDay();
  const offset = day === 0 ? -6 : 1 - day;
  result.setDate(result.getDate() + offset);
  return result;
}

/** Derive conversion (units/clicks), ASP (sales/units), and ACoS (spend/sales) from base heatmap metrics. */
export function deriveHeatmapMetrics(source: Partial<HeatmapMetrics>): HeatmapMetrics {
  const base = { ...EMPTY_METRICS, ...source };
  const { clicks, units, sales, spend } = base;
  return {
    ...base,
    conversion:
      clicks !== null && clicks > 0 && units !== null && Number.isFinite(units) ? units / clicks : null,
    asp: units !== null && units > 0 && sales !== null && Number.isFinite(sales) ? sales / units : null,
    acos:
      sales !== null && sales > 0 && spend !== null && Number.isFinite(spend) ? (spend / sales) * 100 : null,
  };
}

export function parseHeatmapApiPayload(
  payload: Record<string, unknown> | undefined,
  weekRange: HeatmapWeekRange,
  fallbackTimezone: string
): ParsedHeatmap {
  const payloadDays = Array.isArray(payload?.days) ? payload.days : [];
  const daysByName = new Map<string, HeatmapDay>();
  for (const dayValue of payloadDays) {
    const dayRecord = dayValue as Record<string, unknown>;
    const day = String(dayRecord.day || '');
    if (!day || daysByName.has(day)) continue;
    daysByName.set(day, { day, date: String(dayRecord.date || '') });
  }

  const monday = startOfWeekMonday(new Date(weekRange.startIso));
  const days: HeatmapDay[] = WEEKDAY_ORDER.map((dayName, index) => {
    const existing = daysByName.get(dayName);
    if (existing) return existing;
    const date = new Date(monday);
    date.setDate(monday.getDate() + index);
    return { day: dayName, date: localDateKey(date) };
  });

  const emptyCells: HeatmapCell[] = days.map((day) => ({
    day: day.day,
    date: day.date,
    rowCount: 0,
    aggregated: { ...EMPTY_METRICS },
  }));

  const hours: HeatmapHour[] = Array.from({ length: 24 }, (_, hour) => ({
    hour,
    cells: emptyCells.map((cell) => ({ ...cell, aggregated: { ...EMPTY_METRICS } })),
  }));

  let activeBuckets = numberValue(payload?.activeBuckets);
  const rowCount = numberValue(payload?.rowCount);
  const timezone = String(payload?.timezone || fallbackTimezone);

  const payloadHours = Array.isArray(payload?.hours) ? payload.hours : [];
  payloadHours.forEach((hourValue) => {
    const hourEntry = hourValue as Record<string, unknown>;
    const hour = numberValue(hourEntry.hour);
    if (hour < 0 || hour > 23) return;
    const rawCells = Array.isArray(hourEntry.cells) ? hourEntry.cells : [];
    const cellsByDay = new Map<string, HeatmapCell>();
    for (const cellValue of rawCells) {
      const cell = cellValue as Record<string, unknown>;
      const day = String(cell.day || '');
      if (!day) continue;
      cellsByDay.set(day, {
        day,
        date: String(cell.date || ''),
        rowCount: numberValue(cell.rows),
        aggregated: deriveHeatmapMetrics((cell.aggregated ?? {}) as Partial<HeatmapMetrics>),
      });
    }
    hours[hour] = {
      hour,
      cells: days.map(
        (day) =>
          cellsByDay.get(day.day) ?? {
            day: day.day,
            date: day.date,
            rowCount: 0,
            aggregated: { ...EMPTY_METRICS },
          }
      ),
    };
  });

  if (!activeBuckets) {
    for (const hourRow of hours) {
      for (const cell of hourRow.cells) {
        if (cell.rowCount > 0) activeBuckets += 1;
      }
    }
  }

  return { days, hours, activeBuckets, rowCount, timezone, dayCount: HEATMAP_WEEK_DAYS };
}
